import { createSelector } from '@reduxjs/toolkit';

import {
  getAddedClassHierarchies,
  getModifiedClassAttributes,
  getModifiedClassRelationships,
  getPreviousClass,
} from 'src/containers/ProjectDiagram/utils/draft/draftClass';
import {
  getIsColorIdModified,
  getIsDescriptionModified,
  getIsLabelModified,
} from 'src/containers/ProjectDiagram/utils/draft/draftModelConcept';
import { getModelConceptClassKey } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { diagramSnapshotSelector } from 'src/selectors/diagramSnapshot';
import { memoizeSelectorFactory } from 'src/selectors/memoizeSelectorFactory';
import { projectClassAttributesSelector } from 'src/selectors/projectClassAttributes';

const classDraftHierarchyChangesSelector = (
  projectId: string,
  iri: string | undefined
) =>
  createSelector(diagramSnapshotSelector(projectId), (diagram) => {
    if (!diagram?.model?.modelData?.draftData || !iri) {
      return {
        addedChildren: new Set<string>(),
        addedParents: new Set<string>(),
      };
    }

    const isNewClass = !getPreviousClass(diagram, getModelConceptClassKey(iri));

    const { addedChildren, addedParents } = getAddedClassHierarchies({
      classIri: iri,
      isNewClass,
      diagram,
    });

    return {
      addedChildren,
      addedParents,
    };
  });

const classDraftRelationshipChangesSelector = (
  projectId: string,
  iri: string | undefined
) =>
  createSelector(diagramSnapshotSelector(projectId), (diagram) => {
    if (!diagram?.model?.modelData?.draftData || !iri) {
      return new Set<string>();
    }

    const isNewClass = !getPreviousClass(diagram, getModelConceptClassKey(iri));

    const modifiedRelationships = getModifiedClassRelationships({
      classIri: iri,
      isNewClass,
      diagram,
    });

    return modifiedRelationships;
  });

const classDraftAttributeChangesSelector = (
  projectId: string,
  iri: string | undefined
) =>
  createSelector(
    diagramSnapshotSelector(projectId),
    projectClassAttributesSelector(projectId, iri || ''),
    (diagram, classAttributes) => {
      if (
        !diagram?.model?.modelData?.draftData ||
        !iri ||
        !Object.keys(classAttributes).length
      ) {
        return new Set<string>();
      }

      const isNewClass = !getPreviousClass(
        diagram,
        getModelConceptClassKey(iri)
      );

      const modifiedAttributes = getModifiedClassAttributes({
        classAttributes,
        classIri: iri,
        isNewClass,
        diagram,
      });

      return modifiedAttributes;
    }
  );

export const classDraftChangesSelector = memoizeSelectorFactory(
  (projectId: string, iri: string | undefined) =>
    createSelector(
      diagramSnapshotSelector(projectId),
      classDraftHierarchyChangesSelector(projectId, iri),
      classDraftRelationshipChangesSelector(projectId, iri),
      classDraftAttributeChangesSelector(projectId, iri),
      (
        diagram,
        { addedChildren, addedParents },
        modifiedRelationships,
        modifiedAttributes
      ) => {
        if (!diagram?.model?.modelData?.draftData || !iri) {
          return {
            isColorIdModified: false,
            isDescriptionModified: false,
            isLabelModified: false,
            addedChildren: new Set<string>(),
            addedParents: new Set<string>(),
            modifiedRelationships: new Set<string>(),
            modifiedAttributes: new Set<string>(),
          };
        }

        const isNewClass = !getPreviousClass(
          diagram,
          getModelConceptClassKey(iri)
        );

        const isColorIdModified = getIsColorIdModified({
          iri,
          isNewConcept: isNewClass,
          diagram,
        });

        const isDescriptionModified = getIsDescriptionModified({
          iri,
          isNewConcept: isNewClass,
          diagram,
        });

        const isLabelModified = getIsLabelModified({
          iri,
          isNewConcept: isNewClass,
          diagram,
        });

        return {
          isColorIdModified,
          isDescriptionModified,
          isLabelModified,
          addedChildren,
          addedParents,
          modifiedRelationships,
          modifiedAttributes,
        };
      }
    )
);
